import Compressor from 'compressorjs';
import getCroppedImg, { createImage } from '../components/ProfileHeaderUpload/helpers';
import {
    RICH_MEDIA_SHARE_HEIGHT,
    RICH_MEDIA_SHARE_MAX_BYTES,
    RICH_MEDIA_SHARE_WIDTH,
} from '../constants/richMediaShareImage';

type PixelCrop = { x: number; y: number; width: number; height: number };

interface CompressOptions {
    quality: number;
    maxWidth: number;
    maxHeight: number;
}

function compressBlob(blob: Blob, options: CompressOptions): Promise<Blob> {
    return new Promise((resolve, reject) => {
        new Compressor(blob, {
            quality: options.quality,
            maxWidth: options.maxWidth,
            maxHeight: options.maxHeight,
            convertSize: RICH_MEDIA_SHARE_MAX_BYTES,
            mimeType: 'image/jpeg',
            success: file => resolve(file),
            error: err => reject(err),
        });
    });
}

/** Crop then resize to exact rich-media dimensions (1200×630). */
export async function getCroppedRichMediaShareDataUrl(imageSrc: string, pixelCrop: PixelCrop): Promise<string | null> {
    const cropped = await getCroppedImg(imageSrc, pixelCrop);
    if (!cropped) return null;

    const image = await createImage(cropped);
    const canvas = document.createElement('canvas');
    canvas.width = RICH_MEDIA_SHARE_WIDTH;
    canvas.height = RICH_MEDIA_SHARE_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(image, 0, 0, RICH_MEDIA_SHARE_WIDTH, RICH_MEDIA_SHARE_HEIGHT);
    return canvas.toDataURL('image/jpeg', 0.92);
}

/** Compress a JPEG blob to at most 500KB, stepping quality down if needed. */
export async function compressRichMediaShareBlob(
    blob: Blob,
    maxBytes: number = RICH_MEDIA_SHARE_MAX_BYTES,
): Promise<Blob> {
    const attempts: CompressOptions[] = [
        { quality: 0.85, maxWidth: RICH_MEDIA_SHARE_WIDTH, maxHeight: RICH_MEDIA_SHARE_HEIGHT },
        { quality: 0.72, maxWidth: RICH_MEDIA_SHARE_WIDTH, maxHeight: RICH_MEDIA_SHARE_HEIGHT },
        { quality: 0.6, maxWidth: RICH_MEDIA_SHARE_WIDTH, maxHeight: RICH_MEDIA_SHARE_HEIGHT },
        { quality: 0.48, maxWidth: 960, maxHeight: 504 },
        { quality: 0.38, maxWidth: 840, maxHeight: 441 },
    ];

    let lastBlob = blob;
    for (const attempt of attempts) {
        lastBlob = await compressBlob(blob, attempt);
        if (lastBlob.size <= maxBytes) {
            return lastBlob;
        }
    }

    return lastBlob;
}

export async function processRichMediaShareCrop(
    imageSrc: string,
    pixelCrop: PixelCrop,
): Promise<{ dataUrl: string; blob: Blob } | null> {
    const dataUrl = await getCroppedRichMediaShareDataUrl(imageSrc, pixelCrop);
    if (!dataUrl) return null;

    const response = await fetch(dataUrl);
    const croppedBlob = await response.blob();
    const compressedBlob = await compressRichMediaShareBlob(croppedBlob);
    const compressedDataUrl = await blobToDataUrl(compressedBlob);

    return { dataUrl: compressedDataUrl, blob: compressedBlob };
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
